// A school's public calendar (PMU-265): /s/<slug> for anyone, /embed/s/<slug> inside a school's own
// website. Only what the school marked Everyone, while its sharing is on; no sign-in, no child data.
import { useEffect, useState } from "react";
import { Banner, Button, Spinner, Tabs } from "@vavr/ui";
import { api, ApiError } from "@vavr/screens/shared";
import { CalendarViews, EventList, TimetableGrid, type CalEvent, type Period, type Slot } from "./screens.tsx";

interface PublicData {
  school: { name: string; tz: string; state: string }; today: string; events: CalEvent[];
  schedules: { name: string; is_default: boolean; periods: Period[] }[];
  classes: { room: string; grade: string | null; slots: Slot[] }[];
}

export function PublicCalendar({ slug, embed }: { slug: string; embed?: boolean }) {
  const [d, setD] = useState<PublicData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState(0);
  useEffect(() => {
    api<PublicData>("GET", `/api/v1/public/schools/${encodeURIComponent(slug)}`).then((x) => { setD(x); if (typeof document !== "undefined") document.title = `${x.school.name} · Calendar`; },
      (e: Error) => setError(e instanceof ApiError && e.status === 404 ? "This calendar is not public, or the link has changed. Ask the school for its current link." : e.message));
  }, [slug]);
  if (error) return <div className="pcal-public"><Banner tone="info" title="Calendar not available">{error}</Banner></div>;
  if (!d) return <div className="pcal-public pmu-center"><Spinner label="Loading the calendar" /></div>;
  const origin = typeof location === "undefined" ? "" : location.origin;
  const ics = `${origin}/api/v1/public/schools/${slug}/calendar.ics`;
  const webcal = ics.replace(/^https?:/, "webcal:");
  const usual = d.schedules.find((s) => s.is_default) ?? d.schedules[0];
  const days = [1, 2, 3, 4, 5, ...(d.classes.some((c) => c.slots.some((s) => s.weekday === 0 || s.weekday === 6)) ? [6, 0] : [])];
  const subscribe = (
    <span className="pcal-public__subscribe">
      <Button size="sm" variant="secondary" icon="calendar" onClick={() => { location.href = webcal; }}>Subscribe</Button>
      <a className="pmu-link pmu-small" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`} target="_blank" rel="noopener noreferrer">Google Calendar</a>
      <a className="pmu-link pmu-small" href={ics} download>Download (.ics)</a>
    </span>
  );
  const calendar = <CalendarViews events={d.events} today={d.today} compact={embed} onEvent={undefined} />;
  const timetables = d.classes.length && usual ? (
    <div className="pmu-stack">
      {d.classes.length > 1 && (
        <div className="pcal-switch" role="tablist" aria-label="Class">
          {d.classes.map((c, i) => <button type="button" role="tab" key={c.room} aria-selected={i === room} onClick={() => setRoom(i)}>{c.room}</button>)}
        </div>
      )}
      <h3 className="pcal-public__class">{d.classes[room]!.room}{d.classes[room]!.grade ? ` · Grade ${d.classes[room]!.grade}` : ""}</h3>
      <TimetableGrid periods={usual.periods} slots={d.classes[room]!.slots} days={days} />
    </div>
  ) : null;
  return (
    <div className={`pcal-public${embed ? " pcal-public--embed" : ""}`}>
      <header className="pcal-public__head">
        <div><h1>{d.school.name}</h1><p>School calendar{usual && !embed ? ` · bell times: ${usual.name}` : ""}</p></div>
        {subscribe}
      </header>
      {timetables ? <Tabs label="School calendar" defaultValue="calendar" items={[{ value: "calendar", label: "Calendar", content: calendar }, { value: "timetables", label: "Class timetables", content: timetables }]} /> : calendar}
      {!embed && d.events.length > 0 && <section className="pcal-public__next"><h2>Coming up</h2><EventList events={d.events.filter((e) => e.end_date >= d.today)} limit={5} level={3} /></section>}
      <footer className="pcal-public__foot">{embed ? <a href={`${origin}/s/${slug}`} target="_blank" rel="noopener noreferrer">Open the full calendar</a> : <span>Shared by {d.school.name} with Vavr.</span>}</footer>
    </div>
  );
}
